import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Spinner } from 'react-bootstrap';
import AssetPills from '../AssetPills/AssetPills';
import './ProviderSelector.css';

export default function ProviderSelector({
  id = 'data-provider-select',
  label = 'Select your data source',
  providers = [],
  value,
  onChange,
  onRequireEnv,
  loading = false,
  saving = false,
  error = null,
  includeCustomOption = true,
  helperText,
  coverageWarning,
  disabled = false,
  className = '',
  showCapabilities = true,
}) {
  const currentProvider =
    providers.find((provider) => provider.slug === value) || providers.find((p) => p.slug === 'custom') || null;
  const normalizedValue = typeof value === 'string' ? value.trim().toLowerCase() : currentProvider?.slug?.toLowerCase();
  const isCustomSelected = normalizedValue === 'custom';

  const supportedAssets = Array.isArray(currentProvider?.capabilities?.supported)
    ? currentProvider.capabilities.supported
    : [];

  const handleChange = (event) => {
    const next = event.target.value;
    const providerInfo = providers.find((provider) => provider.slug === next);
    onChange?.(next);
    if (providerInfo?.requiredEnv?.length && typeof onRequireEnv === 'function') {
      onRequireEnv(providerInfo);
    }
  };

  const showErrorState = Boolean(error || coverageWarning);

  return (
    <div className={`provider-selector ${className}`.trim()}>
      <label htmlFor={id} className="lumi-label">
        {label}
      </label>
      <div className={`lumi-select-wrapper provider-dropdown-wrapper${showErrorState ? ' is-error' : ''}`}>
        <select
          id={id}
          className={`lumi-select provider-select-dropdown${showErrorState ? ' is-error' : ''}`}
          value={value}
          onChange={handleChange}
          disabled={disabled || loading || saving}
          aria-invalid={showErrorState}
        >
          {providers.map((provider) => (
            <option key={provider.slug} value={provider.slug}>
              {provider.displayName || provider.name || provider.slug}
            </option>
          ))}
          {includeCustomOption && !providers.some((provider) => provider.slug === 'custom') && (
            <option value="custom">Custom Provider / BYO credentials</option>
          )}
        </select>
        {(loading || saving) && <Spinner animation="border" size="sm" className="ms-2 text-primary" />}
      </div>
      {error && (
        <Alert variant="danger" className="mt-2 mb-2">
          {error}
        </Alert>
      )}
      {helperText && (
        <div className="lumi-helper mt-1">
          {helperText}
        </div>
      )}

      {coverageWarning && (
        <Alert variant="danger" className="data-provider-coverage-alert mt-2 mb-2">
          <div className="fw-semibold">
            {coverageWarning.title}
          </div>
          {coverageWarning.body && (
            <div className="small mt-1">
              {coverageWarning.body}
            </div>
          )}
        </Alert>
      )}

      {currentProvider && !isCustomSelected && showCapabilities && (
        <div className="provider-capabilities-panel">
          <div className="provider-cap-header">
            <span className="provider-cap-title">Supported Assets</span>
          </div>
          <AssetPills
            assets={supportedAssets}
            ariaLabel={`Assets supported by ${currentProvider?.displayName || currentProvider?.name || 'provider'}`}
            emptyText="Coverage details not provided"
            className="provider-cap-badges"
          />
        </div>
      )}

      {isCustomSelected && (
        <div className="provider-capabilities-panel provider-capabilities-panel--custom">
          <div className="provider-cap-header">
            <span className="provider-cap-title">Bring Your Own Provider</span>
          </div>
          <p className="provider-cap-description">
            Configure the provider&apos;s API keys below to unlock every asset class they support.
          </p>
        </div>
      )}
    </div>
  );
}

ProviderSelector.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  providers: PropTypes.arrayOf(PropTypes.shape({
    slug: PropTypes.string.isRequired,
    name: PropTypes.string,
    displayName: PropTypes.string,
    capabilities: PropTypes.shape({
      supported: PropTypes.arrayOf(PropTypes.string),
      label: PropTypes.string,
    }),
    requiredEnv: PropTypes.arrayOf(PropTypes.string),
    envDefaults: PropTypes.object,
  })),
  value: PropTypes.string,
  onChange: PropTypes.func,
  onRequireEnv: PropTypes.func,
  loading: PropTypes.bool,
  saving: PropTypes.bool,
  error: PropTypes.string,
  includeCustomOption: PropTypes.bool,
  helperText: PropTypes.node,
  coverageWarning: PropTypes.shape({
    title: PropTypes.string.isRequired,
    body: PropTypes.node,
  }),
  disabled: PropTypes.bool,
  className: PropTypes.string,
  showCapabilities: PropTypes.bool,
};
